'use client';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  LOCATION_POLYGON_PARAM,
  ZOOM_PARAM,
} from '@/components/map/constance';
import {
  SearchFilters,
  SearchPaginationKeys,
  SearchParams,
} from '@/constants/listing-search-keys';
import {
  applyGuestCountsToSearchParams,
  clearGuestSearchParams,
  hasExplicitAdultsOrChildrenSearchParams,
  parseGuestCountsFromSearchParams,
} from '@/utils/guest-search-params';
import { logSearchSubmit } from '@/utils/search-analytics';
import { useRouter } from 'nextjs-toploader/app';
import type { DateRange } from 'react-day-picker';

/** Format a Date as YYYY-MM-DD without pulling in date-fns. */
function toDateParam(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/;
/** Parse a YYYY-MM-DD URL param without pulling in date-fns. */
function parseDateParam(s: string | null): Date | undefined {
  if (!s || !YYYY_MM_DD.test(s)) return undefined;
  const d = new Date(s + 'T00:00:00');
  return isNaN(d.getTime()) ? undefined : d;
}

const defaultState: SearchContextType = {
  applySearchParams() {},
  clearDateParams() {},
  clearFilters() {},
  navigate() {},
  searchParams: new URLSearchParams(),
  selectedDates: undefined,
  setArrive() {},
  setBathrooms() {},
  setMaxBedrooms() {},
  setMinBedrooms() {},
  setBeds() {},
  setDepart() {},
  setFlexDays() {},
  setFlexWeeks() {},
  setGuests() {},
  setAdults() {},
  setChildren() {},
  guestsInteracted: false,
  markGuestsInteracted() {},
  resetGuestsInteracted() {},
  setMaxPets() {},
  setMaxPrice() {},
  setMinPrice() {},
  setParam() {},
  getParam() {},
  setPets() {},
  setResort() {},
  setSelectedDates() {},
  setUnitTypes() {},
  setUnitViews() {},
  syncSearchParams() {},
  totalActiveFilters: 0,
  totalAppliedFilters: 0,
};

const SearchContext = createContext<SearchContextType>(defaultState);

export const MaxGuests = 20;
export const MaxPets = 2;
export const DefaultAdults = 2;
export const DefaultChildren = 0;
export const DefaultGuests = DefaultAdults + DefaultChildren;

const ListingSearchProvider: React.FC<
  PropsWithChildren<{
    onParamsChange?: (options?: NavigateOptions) => void;
  }>
> = ({ children: providerChildren, onParamsChange }) => {
  const nextSearchParams = useSearchParams();
  const router = useRouter();
  const routerPathname = usePathname();

  // When onParamsChange is set we manage our own URLSearchParams derived from
  // window.location so that (a) we never feed a stale useSearchParams() value
  // into URL-building helpers and (b) Next.js's internal router state is never
  // mutated, which avoids the _rsc refetch.
  const [localSearchParams, setLocalSearchParams] = useState<URLSearchParams>(
    () => new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '')
  );
  const searchParams = (onParamsChange ? localSearchParams : nextSearchParams) as URLSearchParams;

  // Keep localSearchParams fresh on back/forward navigation.
  useEffect(() => {
    if (!onParamsChange) return;
    const onPopState = () => {
      setLocalSearchParams(new URLSearchParams(window.location.search));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [onParamsChange]);

  const navigate = useCallback(
    (url: string, options?: NavigateOptions) => {
      if (onParamsChange) {
        // Pass the __NA flag so Next.js's pushState interceptor recognises this
        // as an "already-handled" navigation and skips the ACTION_RESTORE
        // dispatch that would otherwise trigger an RSC refetch.
        window.history.pushState(
          {
            __NA: true,
            __PRIVATE_NEXTJS_INTERNALS_TREE:
              window.history.state?.__PRIVATE_NEXTJS_INTERNALS_TREE,
          },
          '',
          url
        );
        setLocalSearchParams(
          new URLSearchParams(new URL(url, window.location.origin).search)
        );
        onParamsChange(options);
      } else {
        router.push(url, { scroll: false, ...options });
      }
    },
    [onParamsChange, router]
  );

  // Seaarch Params ------------
  const [resort, setResort] = useState<string | undefined>(undefined);
  const [arrive, setArrive] = useState<Date | undefined>(undefined);
  const [depart, setDepart] = useState<Date | undefined>(undefined);
  const selectedDates = useMemo<DateRange | undefined>(() => {
    if (arrive || depart) {
      return {
        from: arrive,
        to: depart,
      };
    }
    return;
  }, [arrive, depart]);
  const [flexDays, setFlexDays] = useState<number | undefined>(undefined);
  const [flexWeeks, setFlexWeeks] = useState<number | undefined>(undefined);
  const [adults, setAdultsState] = useState<number | undefined>(undefined);
  const [children, setChildrenState] = useState<number | undefined>(undefined);
  const [guestsInteracted, setGuestsInteracted] = useState(() =>
    hasExplicitAdultsOrChildrenSearchParams(searchParams)
  );
  const markGuestsInteracted = useCallback(() => {
    setGuestsInteracted(true);
  }, []);
  const resetGuestsInteracted = useCallback(() => {
    setGuestsInteracted(false);
  }, []);
  const guests = useMemo(
    () => (adults ?? DefaultAdults) + (children ?? DefaultChildren),
    [adults, children]
  );

  const handleSetAdults = (count?: number) => {
    if (count === undefined) {
      setAdultsState(undefined);
      return;
    }
    setAdultsState(Math.max(1, Math.min(count, MaxGuests)));
  };

  const handleSetChildren = (count?: number) => {
    if (count === undefined) {
      setChildrenState(undefined);
      return;
    }
    setChildrenState(Math.max(0, Math.min(count, MaxGuests)));
  };

  const guestSearchDefaults = useMemo(
    () => ({ adults: DefaultAdults, children: DefaultChildren }),
    []
  );

  const applyGuestCounts = useCallback(
    (current: URLSearchParams) => {
      const hasExplicitGuestState =
        guestsInteracted || adults !== undefined || children !== undefined;

      if (!hasExplicitGuestState) {
        clearGuestSearchParams(current);
        return;
      }

      applyGuestCountsToSearchParams(
        current,
        adults ?? DefaultAdults,
        children ?? DefaultChildren,
        guestSearchDefaults,
        { explicitDefaults: guestsInteracted }
      );
    },
    [adults, children, guestSearchDefaults, guestsInteracted]
  );

  /** Back-compat: single guest count maps to adults with no children. */
  const handleSetGuests = (guestCount?: number) => {
    if (guestCount === undefined) {
      setAdultsState(undefined);
      setChildrenState(undefined);
      return;
    }
    const val = getGuestValue(guestCount);
    setAdultsState(val);
    setChildrenState(DefaultChildren);
  };
  const [pets, setPets] = useState<number | undefined>(undefined);
  // Seaarch Params ------------ end
  //  Filter Params ------------
  const [bathrooms, setBathrooms] = useState<number | undefined>(undefined);
  const [maxBedrooms, setMaxBedrooms] = useState<number | undefined>(undefined);
  const [minBedrooms, setMinBedrooms] = useState<number | undefined>(undefined);
  const [beds, setBeds] = useState<number | undefined>(undefined);
  const [features, setFeatures] = useState<string[] | undefined>(undefined);
  const [maxGuests, setMaxGuests] = useState<number | undefined>(undefined);
  const [maxPets, setMaxPets] = useState<number | undefined>(undefined);
  const [maxAmount, setMaxAmount] = useState<number | undefined>(undefined);
  const [minAmount, setMinAmount] = useState<number | undefined>(undefined);
  const [unitTypes, setUnitTypes] = useState<string[] | undefined>(undefined);
  const [unitViews, setUnitViews] = useState<string[] | undefined>(undefined);

  const totalActiveFilters = useMemo(() => {
    let activeFilters = 0;
    if (minAmount || maxAmount) {
      activeFilters++;
    }
    if (bathrooms) {
      activeFilters++;
    }
    if (maxBedrooms || minBedrooms) {
      activeFilters++;
    }
    if (beds) {
      activeFilters++;
    }
    if (features && features.length) {
      activeFilters += features.length;
    }
    if (maxGuests) {
      activeFilters++;
    }
    if (maxPets) {
      activeFilters++;
    }
    if (pets) {
      activeFilters++;
    }
    if (unitTypes && unitTypes.length) {
      activeFilters += unitTypes.length;
    }
    if (unitViews && unitViews.length) {
      activeFilters += unitViews.length;
    }
    return activeFilters;
  }, [
    bathrooms,
    // bedrooms,
    maxBedrooms,
    minBedrooms,
    beds,
    features,
    maxGuests,
    maxPets,
    maxAmount,
    minAmount,
    pets,
    unitTypes,
    unitViews,
  ]);
  const totalAppliedFilters = useMemo(() => {
    let appliedFilters = defaultState.totalAppliedFilters;
    if (
      searchParams.has(SearchFilters.MIN_AMOUNT) ||
      searchParams.has(SearchFilters.MAX_AMOUNT)
    ) {
      appliedFilters++;
    }
    if (searchParams.has(SearchFilters.BEDROOMS)) {
      appliedFilters++;
    }
    if (searchParams.has(SearchFilters.BATHROOMS)) {
      appliedFilters++;
    }
    if (searchParams.has(SearchFilters.BEDS)) {
      appliedFilters++;
    }
    if (searchParams.has(SearchFilters.UNIT_VIEWS)) {
      appliedFilters += searchParams
        .get(SearchFilters.UNIT_VIEWS)!
        .split(',').length;
    }
    if (searchParams.has(SearchFilters.FEATURES)) {
      appliedFilters += searchParams
        .get(SearchFilters.FEATURES)!
        .split(',').length;
    }
    if (searchParams.has(SearchFilters.MAX_GUESTS)) {
      appliedFilters++;
    }
    if (searchParams.has(SearchFilters.MAX_PETS)) {
      appliedFilters++;
    }
    if (searchParams.has(SearchFilters.PETS)) {
      appliedFilters++;
    }
    if (searchParams.has(SearchFilters.UNIT_TYPES)) {
      appliedFilters += searchParams
        .get(SearchFilters.UNIT_TYPES)!
        .split(',').length;
    }
    return appliedFilters;
  }, [searchParams]);
  //  Filter Params ------------ end

  const setSelectedDates = (dates?: DateRange) => {
    setMaxAmount(undefined);
    setMinAmount(undefined);
    if (dates?.from) {
      if (!dates) {
        setArrive(undefined);
        setDepart(undefined);
        return;
      }
      if (dates?.from instanceof Date) {
        setArrive(dates.from);
      } else {
        setArrive(undefined);
      }
      if (dates?.to instanceof Date) {
        setDepart(dates.to);
      } else {
        setDepart(undefined);
      }
    }
  };
  const clearDateParams = (
    shouldSubmit?: boolean,
    quickSearchTarget?: string,
    options?: NavigateOptions
  ) => {
    setFlexDays(undefined);
    setFlexWeeks(undefined);
    setMinAmount(undefined);
    setMaxAmount(undefined);
    setArrive(undefined);
    setDepart(undefined);
    if (shouldSubmit) {
      const routerSearchTarget = quickSearchTarget || routerPathname;
      const current = new URLSearchParams(searchParams.toString());
      current.delete(SearchPaginationKeys.SIZE);
      current.delete(SearchPaginationKeys.OFFSET);
      current.delete(SearchParams.ARRIVE);
      current.delete(SearchParams.DEPART);
      current.delete(SearchParams.FLEX_DAYS);
      current.delete(SearchParams.FLEX_WEEKS);
      current.delete(SearchFilters.MAX_AMOUNT);
      current.delete(SearchFilters.MIN_AMOUNT);
      navigate(
        `${routerSearchTarget}${current.toString() ? `?${current.toString()}` : ''}`,
        options
      );
    }
  };

  const getParam = (param: SearchParamsValues | SearchFiltersValues) => {
    switch (param) {
      case SearchParams.ARRIVE:
        return arrive;
      case SearchParams.DEPART:
        return depart;
      case SearchParams.FLEX_DAYS:
        return flexDays;
      case SearchParams.FLEX_WEEKS:
        return flexWeeks;
      case SearchParams.GUESTS:
        return guests;
      case SearchParams.ADULTS:
        return adults;
      case SearchParams.CHILDREN:
        return children;
      case SearchParams.PETS:
        return pets;
      case SearchParams.RESORT:
        return resort;
      case SearchFilters.MIN_AMOUNT:
        return minAmount;
      case SearchFilters.MAX_AMOUNT:
        return maxAmount;
      case SearchFilters.BEDROOMS:
        return [minBedrooms, maxBedrooms];
      case SearchFilters.BATHROOMS:
        return bathrooms;
      case SearchFilters.BEDS:
        return beds;
      case SearchFilters.FEATURES:
        return features;
      case SearchFilters.UNIT_VIEWS:
        return unitViews;
      case SearchFilters.UNIT_TYPES:
        return unitTypes;
      case SearchFilters.MAX_GUESTS:
        return maxGuests;
      case SearchFilters.MAX_PETS:
        return maxPets;
      default:
        return;
    }
  };
  const setParam = (
    param: SearchParamsValues | SearchFiltersValues,
    value?: string | number | Date | string[] | number[],
    shouldSubmit?: boolean,
    quickSearchTarget?: string,
    options?: NavigateOptions
  ) => {
    switch (param) {
      case SearchParams.ARRIVE:
        setArrive(value as Date);
        break;
      case SearchParams.DEPART:
        setDepart(value as Date);
        break;
      case SearchParams.FLEX_DAYS:
        setFlexDays(value as number);
        break;
      case SearchParams.FLEX_WEEKS:
        setFlexWeeks(value as number);
        break;
      case SearchParams.GUESTS: {
        if (value === undefined || value == guests) {
          handleSetGuests(undefined);
          break;
        }
        handleSetGuests(value as number);
        break;
      }
      case SearchParams.ADULTS: {
        if (value === undefined) {
          handleSetAdults(undefined);
          break;
        }
        handleSetAdults(value as number);
        break;
      }
      case SearchParams.CHILDREN: {
        if (value === undefined) {
          handleSetChildren(undefined);
          break;
        }
        handleSetChildren(value as number);
        break;
      }
      case SearchParams.RESORT:
        setResort(value as string);
        break;
      case SearchParams.PETS: {
        if (value === undefined || value == pets) {
          setPets(undefined);
          break;
        }
        setPets(value as number);
        break;
      }
      case SearchFilters.MIN_AMOUNT: {
        const numValue = Number(value); // Ensure value is treated as a number

        if (isNaN(numValue)) {
          setMinAmount(undefined);
        } else {
          setMinAmount(numValue);
        }
        break;
      }
      case SearchFilters.MAX_AMOUNT: {
        const numValue = Number(value); // Ensure value is treated as a number

        if (isNaN(numValue)) {
          setMaxAmount(undefined);
        } else {
          if (minAmount && numValue < minAmount) {
            setMaxAmount(undefined);
          } else {
            setMaxAmount(numValue);
          }
        }
        break;
      }
      case SearchFilters.BEDROOMS:
        if ((value as any)[1]) {
          setMinBedrooms(0);
          setMaxBedrooms(Number((value as any)[1]));
        } else {
          setMaxBedrooms(undefined);
        }
        if ((value as any)[0]) {
          setMinBedrooms(Number((value as any)[0]));
        } else {
          setMinBedrooms(undefined);
        }
        break;
      case SearchFilters.BATHROOMS:
        setBathrooms(value as number);
        break;
      case SearchFilters.BEDS:
        setBeds(value as number);
        break;
      case SearchFilters.FEATURES: {
        if (!value) {
          setFeatures(undefined);
          break;
        }
        const uniqueFeatures = new Set(features);
        if (Array.isArray(value)) {
          (value as string[]).forEach((view) => {
            if (uniqueFeatures.has(view)) {
              uniqueFeatures.delete(view);
            } else {
              uniqueFeatures.add(view);
            }
          });
        } else {
          if (uniqueFeatures.has(value as string)) {
            uniqueFeatures.delete(value as string);
          } else {
            uniqueFeatures.add(value as string);
          }
        }
        setFeatures(Array.from(uniqueFeatures));
        break;
      }
      case SearchFilters.UNIT_VIEWS: {
        if (!value) {
          setUnitViews(undefined);
          break;
        }
        const uniqueUnitViews = new Set(unitViews);
        if (Array.isArray(value)) {
          (value as string[]).forEach((view) => {
            if (uniqueUnitViews.has(view)) {
              uniqueUnitViews.delete(view);
            } else {
              uniqueUnitViews.add(view);
            }
          });
        } else {
          if (uniqueUnitViews.has(value as string)) {
            uniqueUnitViews.delete(value as string);
          } else {
            uniqueUnitViews.add(value as string);
          }
        }
        if (uniqueUnitViews.size === 0) {
          setUnitViews(undefined);
          break;
        }
        setUnitViews(Array.from(uniqueUnitViews));
        break;
      }
      case SearchFilters.UNIT_TYPES: {
        if (!value) {
          setUnitTypes(undefined);
          break;
        }
        const uniqueUnitTypes = new Set(unitTypes);
        if (uniqueUnitTypes.has(value as string)) {
          uniqueUnitTypes.delete(value as string);
        } else {
          uniqueUnitTypes.add(value as string);
        }
        if (uniqueUnitTypes.size === 0) {
          setUnitTypes(undefined);
          break;
        }
        setUnitTypes(Array.from(uniqueUnitTypes));
        break;
      }
      case SearchFilters.MAX_GUESTS:
        if (!value || value == maxGuests) {
          setMaxGuests(undefined);
          break;
        }
        setMaxGuests(value as number);
        break;
      case SearchFilters.MAX_PETS:
        setMaxPets(value as number);
        break;
      default:
        break;
    }
    if (shouldSubmit) {
      const routerSearchTarget = quickSearchTarget || routerPathname;
      const current = new URLSearchParams(searchParams.toString());
      current.delete(SearchPaginationKeys.OFFSET);
      if (!value) {
        if (param === SearchParams.GUESTS) {
          clearGuestSearchParams(current);
        } else if (
          param === SearchParams.ADULTS ||
          param === SearchParams.CHILDREN
        ) {
          const nextAdults =
            param === SearchParams.ADULTS
              ? DefaultAdults
              : (adults ?? DefaultAdults);
          const nextChildren =
            param === SearchParams.CHILDREN
              ? DefaultChildren
              : (children ?? DefaultChildren);
          applyGuestCountsToSearchParams(current, nextAdults, nextChildren, {
            adults: DefaultAdults,
            children: DefaultChildren,
          }, { explicitDefaults: guestsInteracted });
        } else {
          current.delete(param);
        }
      } else if (typeof value === 'number') {
        if (
          param === SearchParams.GUESTS ||
          param === SearchParams.ADULTS ||
          param === SearchParams.CHILDREN
        ) {
          const nextAdults =
            param === SearchParams.GUESTS
              ? getGuestValue(value)
              : param === SearchParams.ADULTS
                ? (value as number)
                : (adults ?? DefaultAdults);
          const nextChildren =
            param === SearchParams.GUESTS
              ? DefaultChildren
              : param === SearchParams.CHILDREN
                ? (value as number)
                : (children ?? DefaultChildren);
          applyGuestCountsToSearchParams(current, nextAdults, nextChildren, {
            adults: DefaultAdults,
            children: DefaultChildren,
          }, { explicitDefaults: guestsInteracted });
        } else {
          current.set(param, value.toString());
        }
      } else if (value instanceof Date) {
        current.set(param, toDateParam(value as Date));
      } else if (Array.isArray(value)) {
        current.set(param, value.join(','));
      } else {
        current.set(param, value as string);
      }
      logSearchSubmit(current);
      navigate(
        `${routerSearchTarget}${current.toString() ? `?${current.toString()}` : ''}`,
        options
      );
    }
  };

  const applySearchParams = useCallback(
    (quickSearchTarget?: string, options?: NavigateOptions) => {
      const routerSearchTarget = quickSearchTarget || routerPathname;
      const current = new URLSearchParams(searchParams.toString());
      current.delete(SearchPaginationKeys.OFFSET);
      // Search-bar submit is a fresh query — drop map viewport filters so a
      // stale/world polygon cannot zero out date/guest results.
      current.delete(LOCATION_POLYGON_PARAM);
      current.delete(ZOOM_PARAM);
      if (arrive) {
        current.set(SearchParams.ARRIVE, toDateParam(arrive));
      } else {
        current.delete(SearchParams.ARRIVE);
      }
      if (depart) {
        current.set(SearchParams.DEPART, toDateParam(depart));
      } else {
        current.delete(SearchParams.DEPART);
      }
      if (flexDays) {
        current.set(SearchParams.FLEX_DAYS, flexDays.toString());
      } else {
        current.delete(SearchParams.FLEX_DAYS);
      }
      if (flexWeeks) {
        current.set(SearchParams.FLEX_WEEKS, flexWeeks.toString());
      } else {
        current.delete(SearchParams.FLEX_WEEKS);
      }
      applyGuestCounts(current);
      if (resort) {
        current.set(SearchParams.RESORT, resort);
      } else {
        current.delete(SearchParams.RESORT);
      }
      if (pets) {
        current.set(SearchParams.PETS, pets.toString());
      } else {
        current.delete(SearchParams.PETS);
      }
      if (maxGuests) {
        current.set(SearchFilters.MAX_GUESTS, maxGuests.toString());
      } else {
        current.delete(SearchFilters.MAX_GUESTS);
      }
      if (minAmount) {
        current.set(SearchFilters.MIN_AMOUNT, minAmount.toString());
      } else {
        current.delete(SearchFilters.MIN_AMOUNT);
      }
      if (maxAmount) {
        current.set(SearchFilters.MAX_AMOUNT, maxAmount.toString());
      } else {
        current.delete(SearchFilters.MAX_AMOUNT);
      }
      if (maxBedrooms || minBedrooms) {
        const bedrooms: string[] = [];
        if (minBedrooms) {
          bedrooms.push(minBedrooms.toString());
        }
        if (maxBedrooms) {
          if (!minBedrooms) {
            bedrooms.push('0');
            bedrooms.push(maxBedrooms.toString());
          } else if (minBedrooms <= maxBedrooms) {
            bedrooms.push(maxBedrooms.toString());
          }
        }
        current.set(SearchFilters.BEDROOMS, bedrooms.join(','));
      } else {
        current.delete(SearchFilters.BEDROOMS);
      }
      if (bathrooms) {
        current.set(SearchFilters.BATHROOMS, bathrooms.toString());
      } else {
        current.delete(SearchFilters.BATHROOMS);
      }
      if (beds) {
        current.set(SearchFilters.BEDS, beds.toString());
      } else {
        current.delete(SearchFilters.BEDS);
      }
      if (unitViews && Array.isArray(unitViews) && unitViews.length) {
        current.set(SearchFilters.UNIT_VIEWS, unitViews.join(','));
      } else {
        current.delete(SearchFilters.UNIT_VIEWS);
      }
      if (features && Array.isArray(features) && features.length) {
        current.set(SearchFilters.FEATURES, features.join(','));
      } else {
        current.delete(SearchFilters.FEATURES);
      }
      if (maxPets) {
        current.set(SearchFilters.MAX_PETS, maxPets.toString());
      } else {
        current.delete(SearchFilters.MAX_PETS);
      }
      if (unitTypes && Array.isArray(unitTypes)) {
        current.set(SearchFilters.UNIT_TYPES, unitTypes.join(','));
      } else {
        current.delete(SearchFilters.UNIT_TYPES);
      }
      logSearchSubmit(current);
      navigate(
        `${routerSearchTarget}${current.toString() ? `?${current.toString()}` : ''}`,
        options
      );
    },
    [
      arrive,
      bathrooms,
      // bedrooms,
      maxBedrooms,
      minBedrooms,
      beds,
      depart,
      features,
      flexDays,
      flexWeeks,
      applyGuestCounts,
      maxGuests,
      maxPets,
      maxAmount,
      minAmount,
      navigate,
      pets,
      resort,
      routerPathname,
      searchParams,
      unitTypes,
      unitViews,
    ]
  );

  const clearFilters = (
    shouldSubmit?: boolean,
    quickSearchTarget?: string,
    options?: NavigateOptions
  ) => {
    setMinAmount(undefined);
    setMaxAmount(undefined);
    setMaxBedrooms(undefined);
    setMinBedrooms(undefined);
    setBathrooms(undefined);
    setBeds(undefined);
    setUnitViews(undefined);
    setMaxGuests(undefined);
    setFeatures(undefined);
    setMaxPets(undefined);
    setPets(undefined);
    setUnitTypes(undefined);
    if (shouldSubmit) {
      const routerSearchTarget = quickSearchTarget || routerPathname;
      const current = new URLSearchParams(searchParams.toString());
      current.delete(SearchPaginationKeys.SIZE);
      current.delete(SearchPaginationKeys.OFFSET);
      current.delete(SearchFilters.MIN_AMOUNT);
      current.delete(SearchFilters.MAX_AMOUNT);
      current.delete(SearchFilters.BEDROOMS);
      current.delete(SearchFilters.BATHROOMS);
      current.delete(SearchFilters.BEDS);
      current.delete(SearchFilters.UNIT_VIEWS);
      current.delete(SearchFilters.FEATURES);
      current.delete(SearchFilters.MAX_GUESTS);
      current.delete(SearchFilters.MAX_PETS);
      current.delete(SearchFilters.PETS);
      current.delete(SearchFilters.UNIT_TYPES);
      navigate(
        `${routerSearchTarget}${current.toString() ? `?${current.toString()}` : ''}`,
        options
      );
    }
  };

  // Called in a useEffect to sync the context state with the URLSearchParams
  const syncSearchParams = useCallback(() => {
    if (hasExplicitAdultsOrChildrenSearchParams(searchParams)) {
      const { adults: urlAdults, children: urlChildren } =
        parseGuestCountsFromSearchParams(searchParams, {
          adults: DefaultAdults,
          children: DefaultChildren,
        });
      handleSetAdults(urlAdults);
      handleSetChildren(urlChildren);
      setGuestsInteracted(true);
    } else {
      handleSetAdults(undefined);
      handleSetChildren(undefined);
      setGuestsInteracted(false);
    }
    if (searchParams.has(SearchParams.ARRIVE)) {
      setArrive(parseDateParam(searchParams.get(SearchParams.ARRIVE)) ?? defaultState.arrive);
    } else {
      setArrive(defaultState.arrive);
    }
    if (searchParams.has(SearchParams.DEPART)) {
      setDepart(parseDateParam(searchParams.get(SearchParams.DEPART)) ?? defaultState.depart);
    } else {
      setDepart(defaultState.depart);
    }
    if (searchParams.has(SearchParams.FLEX_DAYS)) {
      setFlexDays(parseInt(searchParams.get(SearchParams.FLEX_DAYS)!));
    } else {
      setFlexDays(defaultState.flexDays);
    }
    if (searchParams.has(SearchParams.FLEX_WEEKS)) {
      setFlexWeeks(parseInt(searchParams.get(SearchParams.FLEX_WEEKS)!));
    } else {
      setFlexWeeks(defaultState.flexWeeks);
    }
    if (searchParams.has(SearchParams.RESORT)) {
      setResort(searchParams.get(SearchParams.RESORT)!);
    } else {
      setResort(defaultState.resort);
    }
    if (searchParams.has(SearchParams.PETS)) {
      setPets(parseInt(searchParams.get(SearchParams.PETS)!));
    } else {
      setPets(defaultState.pets);
    }
    if (searchParams.has(SearchFilters.MAX_GUESTS)) {
      setMaxGuests(parseInt(searchParams.get(SearchFilters.MAX_GUESTS)!));
    } else {
      setMaxGuests(defaultState.maxGuests);
    }
    if (searchParams.has(SearchFilters.MIN_AMOUNT)) {
      setMinAmount(parseInt(searchParams.get(SearchFilters.MIN_AMOUNT)!));
    } else {
      setMinAmount(defaultState.minPrice);
    }
    if (searchParams.has(SearchFilters.MAX_AMOUNT)) {
      setMaxAmount(parseInt(searchParams.get(SearchFilters.MAX_AMOUNT)!));
    } else {
      setMaxAmount(defaultState.maxPrice);
    }
    if (searchParams.has(SearchFilters.BEDROOMS)) {
      const [min, max] = searchParams.get(SearchFilters.BEDROOMS)!.split(',');
      const maximum = parseInt(max);
      const minimum = parseInt(min);
      setMaxBedrooms(maximum);
      setMinBedrooms(minimum);
    } else {
      setMinBedrooms(defaultState.minBedrooms);
      setMaxBedrooms(defaultState.maxBedrooms);
    }
    if (searchParams.has(SearchFilters.BATHROOMS)) {
      setBathrooms(parseInt(searchParams.get(SearchFilters.BATHROOMS)!));
    } else {
      setBathrooms(defaultState.bathrooms);
    }
    if (searchParams.has(SearchFilters.BEDS)) {
      setBeds(parseInt(searchParams.get(SearchFilters.BEDS)!));
    } else {
      setBeds(defaultState.beds);
    }
    if (searchParams.has(SearchFilters.UNIT_VIEWS)) {
      setUnitViews(searchParams.get(SearchFilters.UNIT_VIEWS)!.split(','));
    } else {
      setUnitViews(defaultState.unitViews);
    }
    if (searchParams.has(SearchFilters.MAX_PETS)) {
      setMaxPets(parseInt(searchParams.get(SearchFilters.MAX_PETS)!));
    } else {
      setMaxPets(defaultState.maxPets);
    }
    if (searchParams.has(SearchFilters.FEATURES)) {
      setFeatures(searchParams.get(SearchFilters.FEATURES)!.split(','));
    } else {
      setFeatures(defaultState.features);
    }
    if (searchParams.has(SearchFilters.UNIT_TYPES)) {
      setUnitTypes(searchParams.get(SearchFilters.UNIT_TYPES)!.split(','));
    } else {
      setUnitTypes(defaultState.unitTypes);
    }
  }, [searchParams]);

  useEffect(() => {
    syncSearchParams();
  }, [searchParams]);
  // sync -------------- end

  // Memoize the search state
  const searchState = useMemo(() => {
    return {
      arrive,
      depart,
      flexDays,
      flexWeeks,
      adults,
      children,
      guests,
      guestsInteracted,
      pets,
      resort,
      selectedDates,
    };
  }, [
    arrive,
    depart,
    flexDays,
    flexWeeks,
    adults,
    children,
    guests,
    guestsInteracted,
    pets,
    resort,
    selectedDates,
  ]);
  const filterState = useMemo(() => {
    return {
      bathrooms,
      // bedrooms,
      maxBedrooms,
      minBedrooms,
      beds,
      features,
      maxGuests,
      maxPets,
      maxPrice: maxAmount,
      minPrice: minAmount,
      // selectedCategory,
      totalActiveFilters,
      totalAppliedFilters,
      unitTypes,
      unitViews,
    };
  }, [
    bathrooms,
    // bedrooms,
    maxBedrooms,
    minBedrooms,
    beds,
    features,
    maxGuests,
    maxPets,
    maxAmount,
    minAmount,
    // selectedCategory,
    totalActiveFilters,
    totalAppliedFilters,
    unitTypes,
    unitViews,
  ]);
  // Memoize the search actions
  const searchActions = useMemo(() => {
    return {
      applySearchParams,
      clearDateParams,
      clearFilters,
      navigate,
      setArrive,
      setBathrooms,
      // setBedrooms,
      setMaxBedrooms,
      setMinBedrooms,
      setBeds,
      setDepart,
      // setFeatureCategory,
      setFeatures,
      setFlexDays,
      setFlexWeeks,
      setGuests: handleSetGuests,
      setAdults: handleSetAdults,
      setChildren: handleSetChildren,
      guestsInteracted,
      markGuestsInteracted,
      resetGuestsInteracted,
      setMaxPets,
      setMaxPrice: setMaxAmount,
      setMinPrice: setMinAmount,
      setParam,
      getParam,
      setPets,
      setResort,
      setSelectedDates,
      setUnitTypes,
      setUnitViews,
      syncSearchParams,
    };
  }, [
    applySearchParams,
    clearDateParams,
    clearFilters,
    handleSetAdults,
    handleSetChildren,
    handleSetGuests,
    guestsInteracted,
    markGuestsInteracted,
    resetGuestsInteracted,
    navigate,
    setArrive,
    setBathrooms,
    // setBedrooms,
    setMaxBedrooms,
    setMinBedrooms,
    setBeds,
    setDepart,
    // setFeatureCategory,
    setFeatures,
    setFlexDays,
    setFlexWeeks,
    setMaxPets,
    setMaxAmount,
    setMinAmount,
    setParam,
    getParam,
    setPets,
    setResort,
    setSelectedDates,
    setUnitTypes,
    setUnitViews,
    syncSearchParams,
  ]);

  return (
    <SearchContext.Provider
      value={{
        searchParams,
        ...searchState,
        ...filterState,
        ...searchActions,
      }}
    >
      {providerChildren}
    </SearchContext.Provider>
  );
};

export const useSearch: () => SearchContextType = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

const getGuestValue = (guests?: number) => {
  if (!guests) {
    return DefaultGuests;
  }
  if (guests > MaxGuests) {
    return MaxGuests;
  }
  return guests;
};

export default ListingSearchProvider;
